import React, { createContext, useState, useContext, useCallback, useEffect, useRef } from 'react';
import { getVinculoEmpresa } from '../services/api';
import { AuthContext } from './auth';

const EmpresaContext = createContext();

export const useEmpresa = () => {
    const context = useContext(EmpresaContext);
    if (!context) {
        throw new Error('useEmpresa deve ser usado dentro de um EmpresaProvider');
    }
    return context;
};

export const EmpresaProvider = ({ children }) => {
    const { authenticated, loading: authLoading } = useContext(AuthContext);
    const [empresaConfig, setEmpresaConfig] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    // Usar refs para cache e controle de fetch (evita dependency loop no useCallback)
    const cacheRef = useRef({ config: null, lastFetch: null });
    const isLoadingRef = useRef(false);

    // Cache duration: 5 minutos
    const CACHE_DURATION = 5 * 60 * 1000;

    // Função para buscar dados da empresa (sem deps que mudam a cada render)
    const fetchEmpresaConfig = useCallback(async (forceRefresh = false) => {
        // Verificar cache via ref (não causa re-render)
        const now = Date.now();
        if (!forceRefresh && cacheRef.current.config && cacheRef.current.lastFetch && (now - cacheRef.current.lastFetch < CACHE_DURATION)) {
            process.env.REACT_APP_ISDEVELOP === 'true' && console.log('[EMPRESA-CONTEXT] ✅ Usando dados em cache');
            return cacheRef.current.config;
        }

        // Evitar múltiplas chamadas simultâneas via ref
        if (isLoadingRef.current) {
            process.env.REACT_APP_ISDEVELOP === 'true' && console.log('[EMPRESA-CONTEXT] ⏳ Requisição já em andamento');
            return cacheRef.current.config;
        }

        isLoadingRef.current = true;
        setIsLoading(true);
        try {
            process.env.REACT_APP_ISDEVELOP === 'true' && console.log('[EMPRESA-CONTEXT] 🔄 Buscando dados da empresa...');
            const response = await getVinculoEmpresa();
            const vinculo = response.data.vinculo;

            const config = {
                _id: vinculo._id,
                id_empresa: vinculo.id_empresa,
                cnpj: vinculo.cnpj,
                name: vinculo.name,
                tipoImpressao: vinculo.tipo_impressao,
                tempoBalcaoMin: parseInt(vinculo.tempoBalcaoMin),
                tempoBalcaoMax: parseInt(vinculo.tempoBalcaoMax),
                tempoEntregaMin: parseInt(vinculo.tempoEntregaMin),
                tempoEntregaMax: parseInt(vinculo.tempoEntregaMax),
                hasCancelPassword: vinculo.has_cancel_password,
                fechamentoTemporario: vinculo.fechamento_temporario,
                statusBot: vinculo.status_bot,
                callAtendente: vinculo.call_atendente,
                statusPrinter: vinculo.status_printer,
                timezone: vinculo.timezone,
                preferredLanguage: vinculo.preferredLanguage,
                customer_asaas: vinculo.customer_asaas,
            };

            cacheRef.current = { config, lastFetch: Date.now() };
            setEmpresaConfig(config);
            process.env.REACT_APP_ISDEVELOP === 'true' && console.log('[EMPRESA-CONTEXT] ✅ Dados atualizados com sucesso');
            return config;
        } catch (error) {
            process.env.REACT_APP_ISDEVELOP === 'true' && console.error('[EMPRESA-CONTEXT] ❌ Erro ao buscar dados:', error);
            return cacheRef.current.config;
        } finally {
            isLoadingRef.current = false;
            setIsLoading(false);
        }
    }, []); // Sem deps mutáveis - usa refs internamente

    // Carregar dados APENAS quando auth estiver pronto e usuário autenticado
    useEffect(() => {
        if (authLoading) {
            process.env.REACT_APP_ISDEVELOP === 'true' && console.log('[EMPRESA-CONTEXT] ⏸️ Aguardando auth inicializar...');
            return;
        }

        if (authenticated) {
            process.env.REACT_APP_ISDEVELOP === 'true' && console.log('[EMPRESA-CONTEXT] 🚀 Auth pronto - buscando dados da empresa');
            fetchEmpresaConfig();
        } else {
            process.env.REACT_APP_ISDEVELOP === 'true' && console.log('[EMPRESA-CONTEXT] ⏸️ Usuário não autenticado');
            // Limpar dados quando deslogado
            cacheRef.current = { config: null, lastFetch: null };
            setEmpresaConfig(null);
        }
    }, [authenticated, authLoading, fetchEmpresaConfig]);

    // Função para atualizar um campo específico sem refetch
    const updateEmpresaConfig = useCallback((updates) => {
        setEmpresaConfig(prev => {
            if (!prev) return null;
            const updated = { ...prev, ...updates };
            cacheRef.current.config = updated;
            return updated;
        });
        process.env.REACT_APP_ISDEVELOP === 'true' && console.log('[EMPRESA-CONTEXT] 🔄 Configuração atualizada localmente:', updates);
    }, []);

    // Função para forçar refresh
    const refreshEmpresaConfig = useCallback(() => {
        return fetchEmpresaConfig(true);
    }, [fetchEmpresaConfig]);

    const value = {
        empresaConfig,
        isLoading,
        fetchEmpresaConfig,
        updateEmpresaConfig,
        refreshEmpresaConfig
    };

    return (
        <EmpresaContext.Provider value={value}>
            {children}
        </EmpresaContext.Provider>
    );
};

export default EmpresaContext;
